import { useNavigate } from 'react-router-dom';
import DirectInputContent from './DirectInputContent';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWineContext } from './useWineContext';
import { toast } from 'react-hot-toast';
import BreadCrumb from '../../components/BreadCrumb';
import Button from '../../components/Buttons';
import InputGroup from '../../components/FormElements/InputGroup';
import { AddIcon, CloseIcon } from '../../assets/Icons';
import MergeWinesModal from './MergeWinesModal';
import ProducerModal from './ProducerModal';
import { useDirectInputApi } from '../../store/api/hooks/directInputApiHook';
import { promisifyAll } from '../../_common/functions';
import _, { set } from 'lodash';
import { URLS } from '../../_config';
import { Building2, Merge, Wine } from 'lucide-react';
import { useNavigationWarning, navigationWarningManager } from '../../hooks/useNavigationWarning';
import Badge from '../../components/Badge';
import RedSliderSwitch from '../../components/FormComponents/RedSliderSwitch';

const DirectInput: React.FC = () => {
    const navigate = useNavigate();
    const { getWineById, getProducerById, getWines, getProducer, getWordAliases, getWordEliminations, getWordChanges, getKeywordSubstitutions,getReservedWords,getVintages } = useDirectInputApi();

    // Refs for preventing duplicate calls and debounce cleanup
    const isInitialMount = useRef(true);
    const isFetchingRef = useRef(false);
    const debouncedFetchRef = useRef<any>(null);

    // UI State
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [showProducerModal, setShowProducerModal] = useState(false);
    const [selectedProducerId, setSelectedProducerId] = useState<string | null>(null);

    // Context State
    const {
        keywordsList,
        setKeywordsList,
        filter_id,
        setfilter_id,
        lockedProducerLiterals,
        setLockedProducerLiterals,
        search_partial,
        setSearch_partial,
        search_partialProducer,
        setSearch_partialProducer
    } = useWineContext();

    // Local State
    const [newNarrowKeyword, setNewNarrowKeyword] = useState('');
    const [selectedWineRows, setSelectedWineRows] = useState<any[]>([]);
    const [fetchedWines, setFetchedWines] = useState<any[]>([]);
    const [fetchedProducers, setFetchedProducers] = useState<any[]>([]);
    const [mergeModalWines, setMergeModalWines] = useState<any[]>([]);

    // Pagination State
    const [winesPage, setWinesPage] = useState(1);
    const [producersPage, setProducersPage] = useState(1);
    const [hasMoreWines, setHasMoreWines] = useState(true);
    const [hasMoreProducers, setHasMoreProducers] = useState(true);
    const [totalRowsOfWines, setTotalRowsOfWines] = useState(0);
    const [totalRowsOfProducers, setTotalRowsOfProducers] = useState(0);

    // Derived counts for badges (set to 0 if no data)
    const winesBadgeCount = fetchedWines.length === 0 ? 0 : totalRowsOfWines;
    const producersBadgeCount = fetchedProducers.length === 0 ? 0 : totalRowsOfProducers;

    // Loading State
    const [isLoadingWines, setIsLoadingWines] = useState(false);
    const [isLoadingProducers, setIsLoadingProducers] = useState(false);
    const [isLoadingMoreWines, setIsLoadingMoreWines] = useState(false);
    const [isLoadingMoreProducers, setIsLoadingMoreProducers] = useState(false);

    // Navigation warning protection
    const hasActiveWork = keywordsList.length > 0 || filter_id !== '' || fetchedWines.length > 0 || fetchedProducers.length > 0 || selectedWineRows.length > 0;
    
    // Register/unregister this page for navigation protection
    useEffect(() => {
        const pageId = 'direct-input';
        navigationWarningManager.registerPage(pageId, hasActiveWork);

        return () => {
            navigationWarningManager.unregisterPage(pageId);
        };
    }, [hasActiveWork]);

    // Use the navigation warning hook
    const { navigateWithWarning } = useNavigationWarning({
        hasUnsavedWork: hasActiveWork
    });

    /**
     * Fetches wine details by array of IDs for merge functionality
     * @param ids - Array of wine IDs to fetch
     * @returns Promise resolving to array of wine data
     */
    const fetchWineById = async (ids: string[]): Promise<any[]> => {
        try {
            const wineDetails = await Promise.all(ids.map(id => getWineById(id)));
            return wineDetails.map(res => res?.data?.data || {});
        } catch (err) {
            console.error('Failed to fetch wine details:', err);
            return [];
        }
    };

    /**
     * Fetches wines from API with pagination support
     * @param keywords - Array of keywords for search
     * @param fieldID - ID filter value
     * @param producers - Array of locked producer literals
     * @param page - Page number for pagination
     * @param append - Whether to append to existing data or replace
     */
    const fetchWines = async (
        keywords: string[] = keywordsList,
        fieldID: string = filter_id,
        producers: string[] = lockedProducerLiterals,
        page: number = 1,
        append: boolean = false
    ): Promise<void> => {
        try {
            // Set appropriate loading state
            if (append) {
                setIsLoadingMoreWines(true);
            } else {
                setIsLoadingWines(true);
            }
            
            const per_page = 40;
            const params: any = {
                keywords: keywords.join(','),
                filter_id: fieldID || '',
                producers: producers.join(','),
                search_partial,
                page,
                per_page
            };
            
            const data = await getWines(params);
            setTotalRowsOfWines(data?.data?.data?.total);
            const result = Array.isArray(data) ? data : (data?.data?.data?.result || []);
            
            // Update wines data based on append flag
            if (append) {
                setFetchedWines(prev => [...prev, ...result]);
            } else {
                setFetchedWines(result);
                setWinesPage(1);
            }
            
            // Update pagination state
            setHasMoreWines(result.length === per_page);
            
        } catch (error) {
            console.error('Failed to fetch wines:', error);
            setHasMoreWines(false);
        } finally {
            // Clear loading states
            if (append) {
                setIsLoadingMoreWines(false);
            } else {
                setIsLoadingWines(false);
            }
        }
    };

    /**
     * Fetches producers from API with pagination support
     * @param keywords - Array of keywords for search
     * @param fieldID - ID filter value
     * @param producers - Array of locked producer literals
     * @param page - Page number for pagination
     * @param append - Whether to append to existing data or replace
     */
    const fetchProducers = async (
        keywords: string[] = keywordsList,
        fieldID: string = filter_id,
        producers: string[] = lockedProducerLiterals,
        page: number = 1,
        append: boolean = false
    ): Promise<void> => {
        try {
            // Set appropriate loading state
            if (append) {
                setIsLoadingMoreProducers(true);
            } else {
                setIsLoadingProducers(true);
            }
            
            const per_page = 40;
            const params: any = {
                keywords: keywords.join(','),
                filter_id: fieldID || '',
                producers: producers.join(','),
                search_partial: search_partialProducer,
                page,
                per_page
            };
            
            const data = await getProducer(params);
            setTotalRowsOfProducers(data?.data?.data?.total);
            const result = Array.isArray(data) ? data : (data?.data?.data?.result || []);
            
            // Update producers data based on append flag
            if (append) {
                setFetchedProducers(prev => [...prev, ...result]);
            } else {
                setFetchedProducers(result);
                setProducersPage(1);
            }
            
            // Update pagination state
            setHasMoreProducers(result.length === per_page);
            
        } catch (error) {
            console.error('Failed to fetch producers:', error);
            setHasMoreProducers(false);
        } finally {
            // Clear loading states
            if (append) {
                setIsLoadingMoreProducers(false);
            } else {
                setIsLoadingProducers(false);
            }
        }
    };

    /**
     * Consolidated function to fetch both wines and producers
     * Prevents duplicate API calls by using a ref to track fetching state
     */
    const fetchBothTablesData = useCallback(async (
        keywords: string[] = keywordsList,
        fieldID: string = filter_id,
        producers: string[] = lockedProducerLiterals,
        page: number = 1,
        append: boolean = false
    ) => {
        // Prevent multiple simultaneous calls
        if (isFetchingRef.current) {
            return;
        }

        try {
            isFetchingRef.current = true;
            
            // Call both APIs concurrently
            await Promise.all([
                fetchWines(keywords, fieldID, producers, page, append),
                fetchProducers(keywords, fieldID, producers, page, append)
            ]);
            
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            isFetchingRef.current = false;
        }
    }, [search_partial, search_partialProducer]);

    /**
     * Create debounced function with proper cleanup
     * Uses useCallback to prevent recreation on every render
     */
    const createDebouncedFetch = useCallback(() => {
        // Cancel previous debounced call if it exists
        if (debouncedFetchRef.current) {
            debouncedFetchRef.current.cancel();
        }
        
        // Create new debounced function
        debouncedFetchRef.current = _.debounce((id: string, keywords: string[], producers: string[]) => {
            if (keywords.length > 0 || id.trim() || producers.length > 0) {
                fetchBothTablesData(keywords, id, producers, 1, false);
            }
        }, 750);
        
        return debouncedFetchRef.current;
    }, [fetchBothTablesData]);

    /**
     * Handles keyword input enter key press to add new keyword
     */
    const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter' && newNarrowKeyword.trim()) {
            e.preventDefault();
            const newKeyword = newNarrowKeyword.trim();
            if (keywordsList.includes(newKeyword)) {
                setNewNarrowKeyword('');
                return; // Do not update state or trigger API if duplicate
            }
            setKeywordsList((prev: string[]) => Array.from(new Set([...prev, newKeyword])));
            setNewNarrowKeyword('');
        }
    };

    /**
     * Removes keyword from keywords list by index
     */
        const removeKeyword = (index: number): void => {
            setKeywordsList((prev: string[]) => {
                const updated = prev.filter((_: string, i: number) => i !== index);
                // If all keywords are cleared, also clear selected producers
                if (updated.length === 0) {
                    setLockedProducerLiterals([]);
                }
                return updated;
            });
        };

    /**
     * Handles ID filter input change with debounced API call
     * Only triggers API after 1.5s of inactivity (debounced)
     */
    const handlefilter_idChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        setfilter_id(value);
        // Always cancel any pending debounced fetch
        if (debouncedFetchRef.current) {
            debouncedFetchRef.current.cancel();
        }
        // If the field is cleared, also clear the data (like keyword search)
        if (!value.trim() && keywordsList.length === 0 && lockedProducerLiterals.length === 0) {
            clearDataIfNoSearchCriteria();
            return;
        }
        // Use the debounced function only if value is not empty
        if (value.trim() || keywordsList.length > 0 || lockedProducerLiterals.length > 0) {
            const debouncedFetch = createDebouncedFetch();
            debouncedFetch(value, keywordsList, lockedProducerLiterals);
        }
    };

    /**
     * Adds word to keywords list if not already present
     */
    const handleWordClick = (word: string): void => {
        if (word) {
            if (keywordsList.includes(word)) {
                return; // Do not update state or trigger API if duplicate
            }
            setKeywordsList((prev: string[]) => Array.from(new Set([...prev, word])));
        }
    };

    /**
     * Adds producer ID to locked list if not already present
     */
    const handleProducerIdClick = (id: string): void => {
        if (id && !lockedProducerLiterals.includes(id)) {
            setLockedProducerLiterals((prev: string[]) => [...prev, id]);
        }
    };

    /**
     * Handles wine row selection with validation for merge functionality
     */
    const handleWineRowSelect = (row: any, checked: boolean): void => {
        setSelectedWineRows(prev => {
            if (checked) {
                if (prev.some(r => r.id === row.id)) return prev;
                if (prev.length >= 3) {
                    toast.error('You can select at most 3 wines for merging.');
                    return prev;
                }
                return [...prev, row];
            } else {
                return prev.filter(r => r.id !== row.id);
            }
        });
    };

    /**
     * Handles merge selected wines action
     */
    const handleMergeSelected = async (): Promise<void> => {
        if (selectedWineRows.length < 1 || selectedWineRows.length > 3) return;
        const ids = selectedWineRows.map(row => row.id);
        const winesData = await fetchWineById(ids);
        console.log('Fetched wines data for merge:', winesData);
        setMergeModalWines(winesData);
        setShowMergeModal(true);
        };

    /**
     * Opens producer edit modal
     */
    const handleEditProducer = (id: string): void => {
        setSelectedProducerId(id);
        setShowProducerModal(true);
    };

    /**
     * Navigates to add wine to producer page
     */
    const handleAddWineToProducer = (producerId: string): void => {
        navigate(`${URLS.WINE_ADDITION.ADD_WINE_TO_PRODUCER.replace(':id', producerId)}`);
    };

    /**
     * Navigates to new producer creation page
     */
    const handleAddNewProducer = (): void => {
        navigate(URLS.WINE_ADDITION.NEW_PRODUCER);
    };

    /**
     * Handles producer modal update completion
     */
    const handleProducerUpdated = (): void => {
        setShowProducerModal(false);
        fetchBothTablesData(keywordsList, filter_id, lockedProducerLiterals, producersPage, false);
    };

    /**
     * Handles merge success completion - refresh wine data and reset selections
     */
    const handleMergeSuccess = (): void => {
        setShowMergeModal(false);
        setSelectedWineRows([]);
        setMergeModalWines([]);
        // Refresh both wines and producers data
        fetchBothTablesData(keywordsList, filter_id, lockedProducerLiterals, 1, false);
    };

    /**
     * Removes locked producer literal by index
     */
    const removeLockProducerLiteral = (index: number): void => {
        setLockedProducerLiterals((prev: string[]) => prev.filter((_: string, i: number) => i !== index));
    };

    /**
     * Clears fetched data when no search criteria are present
     */
    const clearDataIfNoSearchCriteria = (): void => {
        setFetchedWines([]);
        setFetchedProducers([]);
        setSelectedWineRows([]);
    };

    /**
     * Effect to handle immediate data fetching (non-debounced changes)
     * Only for keywordsList, lockedProducerLiterals, and search partial options
     */
    useEffect(() => {
        // Prevent multiple simultaneous calls
        if (isFetchingRef.current) {
            return;
        }

        // Clear selected wines when search parameters change
        setSelectedWineRows([]);

        // Check if we have any search criteria
        const hasSearchCriteria = keywordsList.length > 0 || filter_id.trim() || lockedProducerLiterals.length > 0;

        if (!hasSearchCriteria) {
            clearDataIfNoSearchCriteria();
            return;
        }

        // Fetch data for both tables (immediate for non-filter_id changes)
        fetchBothTablesData(keywordsList, filter_id, lockedProducerLiterals, 1, false);
    }, [
        keywordsList,
        lockedProducerLiterals,
        search_partial,
        search_partialProducer,
        fetchBothTablesData
    ]);

    /**
     * Separate effect for filter_id changes (debounced only)
     * This ensures filter_id changes only trigger debounced calls
     */
    useEffect(() => {
        // Only trigger debounced call if filter_id has value and other criteria exist
        setSelectedWineRows([]);
        if (filter_id.trim() && (keywordsList.length > 0 || lockedProducerLiterals.length > 0)) {
            const debouncedFetch = createDebouncedFetch();
            debouncedFetch(filter_id, keywordsList, lockedProducerLiterals);
        }
    }, [filter_id]); // Only depend on filter_id

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            if (debouncedFetchRef.current) {
                debouncedFetchRef.current.cancel();
            }
        };
    }, []);

    return (
        <div className="main-content-box" id='directInputPage'>
            {/* Header Section */}
            <BreadCrumb
                title="Wine Addition - Direct Input"
                rightContent={
                    <Button
                        variant="dark"
                        size="md"
                        onClick={handleAddNewProducer}
                    >
                       <AddIcon className="w-4 h-4 mr-2" /> Wine Producer
                    </Button>
                }
            />

            <DirectInputContent
                newNarrowKeyword={newNarrowKeyword}
                setNewNarrowKeyword={setNewNarrowKeyword}
                handleKeywordKeyPress={handleKeywordKeyPress}
                filter_id={filter_id}
                handlefilter_idChange={handlefilter_idChange}
                keywordsList={keywordsList}
                removeKeyword={removeKeyword}
                lockedProducerLiterals={lockedProducerLiterals}
                removeLockProducerLiteral={removeLockProducerLiteral}
                fetchedProducers={fetchedProducers}
                hasMoreProducers={hasMoreProducers}
                isLoadingProducers={isLoadingProducers}
                isLoadingMoreProducers={isLoadingMoreProducers}
                handleEditProducer={handleEditProducer}
                handleAddWineToProducer={handleAddWineToProducer}
                handleProducerIdClick={handleProducerIdClick}
                handleWordClick={handleWordClick}
                onLoadMoreProducers={() => {
                    if (!isLoadingMoreProducers && hasMoreProducers) {
                        const nextPage = producersPage + 1;
                        setProducersPage(nextPage);
                        fetchProducers(keywordsList, filter_id, lockedProducerLiterals, nextPage, true);
                    }
                }}
                producersBadgeCount={producersBadgeCount}
                search_partialProducer={search_partialProducer}
                setSearch_partialProducer={setSearch_partialProducer}
                fetchedWines={fetchedWines}
                hasMoreWines={hasMoreWines}
                isLoadingWines={isLoadingWines}
                isLoadingMoreWines={isLoadingMoreWines}
                handleWineRowSelect={handleWineRowSelect}
                selectedWineRows={selectedWineRows}
                onLoadMoreWines={() => {
                    if (!isLoadingMoreWines && hasMoreWines) {
                        const nextPage = winesPage + 1;
                        setWinesPage(nextPage);
                        fetchWines(keywordsList, filter_id, lockedProducerLiterals, nextPage, true);
                    }
                }}
                winesBadgeCount={winesBadgeCount}
                search_partial={search_partial}
                setSearch_partial={setSearch_partial}
                handleMergeSelected={handleMergeSelected}
                shouldShowEditWines={true}
            />

            {/* Modals */}
            {showMergeModal && (
                <MergeWinesModal
                    isOpen={showMergeModal}
                    onClose={() => setShowMergeModal(false)}
                    selectedWines={mergeModalWines}
                    onMergeSuccess={handleMergeSuccess}
                />
            )}

            {showProducerModal && selectedProducerId && (
                <ProducerModal
                    isOpen={showProducerModal}
                    onClose={() => setShowProducerModal(false)}
                    producerId={selectedProducerId}
                    onUpdated={handleProducerUpdated}
                />
            )}
        </div>
    );
};

export default DirectInput;